/**
 * RELÓGIO CONFIÁVEL — segunda leitura da hora, para AUDITORIA. Não carimba nada.
 *
 * ⚠️ LEIA ISTO ANTES DE USAR EM QUALQUER DECISÃO.
 *
 * Este módulo é um OBSERVADOR. O `data_log` do ponto, a competência, o intervalo mínimo
 * entre batidas e todas as demais validações saem do relógio do APARELHO (`Date.now()`),
 * como sempre saíram. O que este arquivo produz viaja em campos SEPARADOS na batida
 * (`hora_confiavel`, `fonte_hora`, `divergencia_seg`, `idade_ms`...) para o backend
 * guardar e o administrativo tratar em relatório.
 *
 * ISSO É CICATRIZ, NÃO PREFERÊNCIA. Já foi o contrário: o `data_log` passou a sair
 * daqui, um furo na detecção de reboot aceitou âncora de outra sessão de boot, e
 * batidas foram gravadas com HORAS de erro em produção — marcadas como confiáveis.
 * A lição não foi "a âncora é ruim": foi que o app não pode ser dono dessa decisão.
 * Enquanto a política vive no cliente, corrigir custa release de loja; no backend,
 * custa um deploy e conserta todos os aparelhos já instalados.
 *
 * **Não volte a plugar `agora()` em nada que decida ou grave hora.**
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * COMO A LEITURA É FEITA: duas fontes que o usuário NÃO consegue falsificar:
 *   1. a hora do SERVIDOR (header `Date` de qualquer resposta da nossa API);
 *   2. o UPTIME do aparelho (ms desde o boot, contador de hardware — imune a mudança
 *      de data, hora e fuso). Ver @/modules/tirvu-clock.
 *
 * Guardamos o par (horaServidor, uptime) como ÂNCORA. A partir dela:
 *
 *     agora() = ancora.servidorMs + (uptimeAgora - ancora.uptimeMs)
 *
 * O relógio de parede não entra na conta. Isso é o que faz funcionar OFFLINE, que é o
 * caso que importa: o app é offline-first e a batida nasce sem rede. Uma âncora não
 * "vence" com o tempo — é um offset; envelhecer só acrescenta a deriva do oscilador
 * (segundos por semana), irrelevante para ponto.
 *
 * ÚNICO FURO: REBOOT. O uptime zera, a âncora morre e caímos no modo DEGRADADO até a
 * próxima resposta da API. Detectamos o caso comum (uptime menor que o da âncora), mas
 * fica um caso cego: reboot + aparelho ligado por mais tempo do que o uptime gravado
 * antes de o app abrir de novo. Por isso o modo degradado ainda tem a MARCA D'ÁGUA (o
 * relógio confiável nunca anda para trás) e por isso a batida carrega `fonteHora`: uma
 * hora não verificada fica VISÍVEL para o RH em vez de passar por boa.
 *
 * ⚠️ SÍNCRONO DE PROPÓSITO. `agora()` substitui `Date.now()` dentro de useMemo e
 * comparações que não podem virar async. A âncora vive em memória (hidratada por
 * `iniciar()`) e o uptime nativo é uma chamada JSI direta.
 *
 * ⚠️ FALLBACK. Sem o módulo nativo (build antigo em campo, web) não há âncora: `agora()`
 * devolve `max(Date.now(), marcaDagua)` e `estado().confiavel` é false. Nada quebra — o
 * app só perde a proteção, e a tela de ponto NÃO bloqueia ninguém por uma divergência
 * que não consegue medir.
 */
import AsyncStorage from "@/platform/async-storage";
import { AppState, type AppStateStatus } from "@/platform/app-state";
// WEB: módulo nativo → shim `@/platform/tirvu-clock` (default com os mesmos métodos; no web
// `uptimeMs()`/`bootCount()` devolvem null → modo DEGRADADO previsto no cabeçalho acima).
import TirvuClock from "@/platform/tirvu-clock";

// ⚠️ SUFIXO v2 É OBRIGATÓRIO — NÃO volte para v1.
//
// A versão anterior aceitava âncora de outra sessão de boot (ver `ancoraValida`) e
// carimbava horas erradas. Pior: ela alimentava a MARCA D'ÁGUA com essas horas erradas,
// e a marca é IRREVERSÍVEL por construção (só avança). Um aparelho cuja âncora podre
// projetou a hora para o FUTURO ficou com a marca envenenada no disco.
//
// Se as chaves continuassem v1, o app corrigido leria essa marca envenenada na
// atualização e seguiria carimbando adiantado — herdando o estrago do build quebrado.
// Trocar a chave descarta o estado corrompido e começa limpo.
const CHAVE_ANCORA = "@tirvumais:relogio-ancora-v2";
const CHAVE_MARCA = "@tirvumais:relogio-marca-v2";

/** Âncora mais nova que isto = hora vinda do servidor "agorinha" (fonte 'S'). */
const FRESCOR_SERVIDOR_MS = 5 * 60 * 1000;

/** Regrava a âncora no disco no máximo a cada 5 min (ela não decai — só reduz deriva). */
const INTERVALO_PERSISTIR_ANCORA_MS = 5 * 60 * 1000;

/** Regrava a marca d'água no disco no máximo a cada 1 min. */
const INTERVALO_PERSISTIR_MARCA_MS = 60 * 1000;

/**
 * Teto de quanto a marca d'água pode estar ADIANTE do relógio do aparelho antes de
 * deixarmos de confiar nela.
 *
 * A marca é IRREVERSÍVEL por construção (só avança). Isso é o que a torna útil contra
 * atrasar o relógio — e é também o seu risco: se um header `Date` corrompido (proxy com
 * relógio quebrado, servidor mal configurado) empurrasse a marca para 2030, o app
 * ficaria PRESO em 2030 para sempre no modo degradado, sem forma de curar a não ser
 * limpando os dados do app. Com o teto, o estrago máximo vira 12h em vez de anos.
 *
 * 12h e não menos: a marca existe para barrar quem atrasa o relógio, e atrasar mais que
 * um turno inteiro já joga a batida para outro dia — erro grosseiro, que o servidor pega
 * na chegada. Abaixo desse limite ela continua fazendo o trabalho dela.
 */
const TETO_MARCA_ADIANTE_MS = 12 * 60 * 60 * 1000;

/**
 * Hora do modo DEGRADADO (sem âncora): o relógio do aparelho, com a marca d'água como
 * piso — mas só enquanto a marca for plausível (ver `TETO_MARCA_ADIANTE_MS`).
 */
const horaDegradada = (): number => {
  const dispositivo = Date.now();
  if (marcaDagua <= 0) return dispositivo;
  if (marcaDagua - dispositivo > TETO_MARCA_ADIANTE_MS) return dispositivo;
  return dispositivo > marcaDagua ? dispositivo : marcaDagua;
};

// Faixa de sanidade para a hora do servidor: blinda contra header `Date` corrompido,
// proxy com relógio quebrado ou resposta servida de um cache antigo. Fora daqui, ignora.
const MIN_PLAUSIVEL_MS = Date.parse("2024-01-01T00:00:00Z");
const MAX_PLAUSIVEL_MS = Date.parse("2100-01-01T00:00:00Z");

const MESES_HTTP: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

/**
 * Converte o header `Date` (IMF-fixdate da RFC 7231: "Sun, 30 Aug 2026 21:36:28 GMT")
 * em ms epoch.
 *
 * ⚠️ NÃO confie só no `Date.parse`. O ECMAScript só OBRIGA a engine a entender o ISO
 * 8601; qualquer outro formato é implementation-defined, e o header HTTP não é ISO. Se o
 * Hermes devolvesse NaN aqui, a âncora NUNCA seria criada e o relógio confiável inteiro
 * viraria no-op SILENCIOSO — o app continuaria carimbando a hora do aparelho sem
 * ninguém perceber. Por isso: tenta o nativo e, se vier NaN, parseia na mão.
 */
const parseHttpDate = (raw: string): number => {
  const nativo = Date.parse(raw);
  if (Number.isFinite(nativo)) return nativo;

  const m =
    /^[A-Za-z]{3},\s+(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{2}):(\d{2}):(\d{2})\s+GMT$/.exec(
      raw.trim(),
    );
  if (!m) return NaN;

  const mes = MESES_HTTP[m[2].toLowerCase()];
  if (mes == null) return NaN;

  return Date.UTC(
    Number(m[3]),
    mes,
    Number(m[1]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6]),
  );
};

/** Abaixo disto a divergência é ruído (latência, deriva normal), não desalinhamento. */
const RUIDO_DIVERGENCIA_MS = 2 * 60 * 1000;

/** Folga para considerar que uma divergência "fecha em hora cheia". */
const TOLERANCIA_HORA_CHEIA_MS = 2 * 60 * 1000;

/**
 * Natureza da divergência entre o relógio do aparelho e o confiável:
 *
 *  'nenhuma' — dentro do ruído (< 2 min).
 *  'fuso'    — fecha em HORA CHEIA (±2 min): 1h, 2h, 3h... É a assinatura de fuso
 *              horário. Ou o aparelho está num fuso diferente do da empresa (base em
 *              Manaus/Acre/Noronha — legítimo), ou alguém empurrou o relógio em horas
 *              redondas justamente para imitar isso.
 *  'relogio'  — não fecha em hora cheia (ex.: 23 min, 1h47). Ninguém "cai" nisso por
 *              fuso: é relógio mexido na mão ou aparelho sem NTP há muito tempo.
 */
export type TipoDivergencia = "nenhuma" | "fuso" | "relogio";

/**
 * Aplica a regra da hora cheia sobre a divergência (em segundos).
 *
 * ⚠️ Isto CLASSIFICA, não corrige. Trocar só o fuso do aparelho não mexe no epoch, então
 * essa divergência é sempre 0 nesse caso — o que ela pega é quem mexeu no RELÓGIO em
 * horas redondas. Para corrigir de fato a hora gravada ainda falta saber o fuso ESPERADO
 * do posto, que hoje não vem do backend.
 */
export const classificarDivergencia = (
  divergenciaSeg: number,
): TipoDivergencia => {
  const ms = Math.abs(divergenciaSeg) * 1000;
  if (ms <= RUIDO_DIVERGENCIA_MS) return "nenhuma";

  const horas = Math.round(ms / 3600000);
  if (horas < 1) return "relogio";

  const resto = Math.abs(ms - horas * 3600000);
  return resto <= TOLERANCIA_HORA_CHEIA_MS ? "fuso" : "relogio";
};

/**
 * Limiar de coerência entre o fuso declarado e a LONGITUDE da batida.
 *
 * A longitude determina a hora SOLAR (offset solar = lon / 15). Um fuso civil legítimo
 * nunca fica longe do solar. Medido nos extremos do Brasil, a MAIOR diferença legítima é
 * **0,93h** (extremo oeste do Pará, que é UTC-3 com solar -3,93); depois vêm o oeste do
 * RS (0,84h) e a costa da PB/RN (0,68h). 1,5h deixa 0,57h de folga: nenhum posto
 * brasileiro cai como falso positivo.
 */
const LIMIAR_COERENCIA_FUSO_H = 1.5;

/** 'sem_posicao' = batida sem GPS (config `registroSemGeo`) — não dá para afirmar nada. */
export type CoerenciaFuso = "sem_posicao" | "coerente" | "incoerente";

/**
 * O fuso que o APARELHO declara bate com o lugar onde a batida realmente aconteceu?
 *
 * Fecha o buraco que a âncora não cobre: trocar o fuso não mexe no epoch, então a
 * divergência de relógio é ZERO nesse caso e nada acusava. A posição é a testemunha
 * independente — e o app já valida a cerca do posto, então a batida normalmente ocorre
 * onde deveria.
 *
 * ⚠️ LIMITES REAIS (medidos, não estimados):
 *  • fuso estrangeiro (≥ 3h, o caso realista) → SEMPRE pego;
 *  • erro de 2h → pego em quase todo o país, escapa em faixa de fronteira (ex.: extremo
 *    oeste do PA, onde o solar fica no meio do caminho);
 *  • erro de 1h → NÃO é separável de um posto legítimo do estado vizinho. Para pegar
 *    isso seria preciso o fuso ESPERADO do posto, que hoje o backend não devolve.
 *
 * Só CLASSIFICA — não corrige a hora e não bloqueia ninguém.
 */
export const coerenciaFusoPosicao = (
  longitude: number | string | null | undefined,
  utcOffsetMin: number,
): CoerenciaFuso => {
  const lon = Number(longitude);
  // 0 cobre "" / null / undefined (Number("") === 0) — e nenhum posto brasileiro fica
  // sobre o meridiano de Greenwich, então não há caso legítimo perdido aqui.
  if (!Number.isFinite(lon) || lon === 0 || lon < -180 || lon > 180) {
    return "sem_posicao";
  }
  const solarH = lon / 15;
  const declaradoH = -utcOffsetMin / 60; // getTimezoneOffset() é minutos a OESTE
  return Math.abs(declaradoH - solarH) > LIMIAR_COERENCIA_FUSO_H
    ? "incoerente"
    : "coerente";
};

/**
 * De onde veio a hora carimbada:
 *  'S' = âncora fresca do servidor (< 5 min) — hora verificada;
 *  'M' = âncora do servidor propagada pelo relógio monotônico (offline) — verificada;
 *  'D' = sem âncora válida, hora do APARELHO. NÃO verificada.
 */
export type FonteHora = "S" | "M" | "D";

interface Ancora {
  /** Hora do servidor (ms epoch) no instante da sincronização. */
  servidorMs: number;
  /** Uptime do aparelho (ms desde o boot) no MESMO instante. */
  uptimeMs: number;
  /** `Date.now()` no mesmo instante — só auditoria (quanto o aparelho já estava errado). */
  dispositivoMs: number;
  /** Sessão de boot em que a âncora nasceu (Android). null = desconhecida (iOS). */
  bootId: number | null;
}

/**
 * A âncora em memória nasceu NESTE processo?
 *
 * ⚠️ INVARIANTE CENTRAL DA CORREÇÃO: um reboot mata o processo. Logo, âncora criada
 * neste processo é NECESSARIAMENTE da sessão de boot atual — o uptime dela é
 * comparável com o de agora, sempre. Âncora lida do DISCO não tem essa garantia, e foi
 * exatamente aí que o app quebrou em produção (ver `ancoraValida`).
 */
let ancoraDestaSessao = false;

export interface EstadoRelogio {
  /** Hora confiável (ms epoch) — o que `agora()` devolveria. */
  ms: number;
  fonte: FonteHora;
  /** true = há âncora válida; só então a divergência significa alguma coisa. */
  confiavel: boolean;
  /** Relógio do aparelho menos o confiável, em segundos. Positivo = aparelho adiantado. */
  divergenciaSeg: number;
  /** Natureza da divergência pela regra da hora cheia. Ver `classificarDivergencia`. */
  tipoDivergencia: TipoDivergencia;
  /** Uptime atual (ms desde o boot), ou null sem o módulo nativo. */
  uptimeMs: number | null;
  /**
   * Offset do fuso do APARELHO, no formato do `getTimezoneOffset()`: minutos a OESTE de
   * UTC (Brasília = 180, Manaus = 240, Acre = 300, Noronha = 120).
   *
   * ⚠️ A âncora protege o INSTANTE, não o fuso: o `data_log` é formatado com os getters
   * locais (`getHours`/`getDate`), então trocar só o fuso do aparelho ainda desloca a
   * hora gravada. Enquanto o cadastro não expuser o fuso esperado do posto, isto fica
   * gravado na batida para o RH conseguir VER a divergência.
   */
  utcOffsetMin: number;
}

let ancora: Ancora | null = null;
/** Maior instante CONFIÁVEL já observado. O relógio nunca anda para trás disto. */
let marcaDagua = 0;
let hidratado = false;
let ancoraPersistidaEm = 0;
let marcaPersistidaEm = 0;

/**
 * Hidrata a âncora e a marca d'água do disco. Chamar UMA vez, cedo (app/_layout.tsx).
 * Antes disso `agora()` já funciona — só devolve o relógio do aparelho.
 */
export const iniciar = async (): Promise<void> => {
  if (hidratado) return;
  hidratado = true;

  try {
    const [rawAncora, rawMarca] = await Promise.all([
      AsyncStorage.getItem(CHAVE_ANCORA),
      AsyncStorage.getItem(CHAVE_MARCA),
    ]);

    // ⚠️ O disco NUNCA atropela o que já está na memória. `iniciar()` é assíncrono e
    // uma resposta da API pode chegar no meio da leitura — sem esta guarda, a âncora
    // FRESCA que acabou de vir do servidor era substituída pela velha do disco (e, no
    // caso de um reboot, por uma inválida, derrubando o app para o modo degradado até a
    // próxima resposta). Disco é só o fallback de quem ainda não sincronizou.
    if (rawMarca) {
      const m = Number(rawMarca);
      if (Number.isFinite(m) && m >= MIN_PLAUSIVEL_MS && m <= MAX_PLAUSIVEL_MS) {
        marcaDagua = Math.max(marcaDagua, m);
      }
    }

    if (rawAncora && !ancora) {
      const a = JSON.parse(rawAncora) as Ancora;
      if (
        Number.isFinite(a?.servidorMs) &&
        Number.isFinite(a?.uptimeMs) &&
        a.servidorMs >= MIN_PLAUSIVEL_MS &&
        a.servidorMs <= MAX_PLAUSIVEL_MS &&
        a.uptimeMs > 0
      ) {
        // ⚠️ PROVISÓRIA. Veio do disco, então pode ser de ANTES de um reboot — e uma
        // âncora de outra sessão de boot produz hora com horas de erro (foi o que
        // quebrou a produção). `ancoraDestaSessao` fica FALSO de propósito: só passa a
        // valer se o `bootCount` provar que é a mesma sessão. Enquanto não provar,
        // `agora()` usa o relógio do aparelho — o comportamento antigo, correto para
        // quem não mexeu em nada.
        ancora = a;
        ancoraDestaSessao = false;
      }
    }
  } catch {
    // Storage ilegível: segue degradado. Uma resposta da API reancora em segundos.
  }

  // Indo para background, grava a marca d'água: o app pode ser morto pelo SO sem
  // aviso, e a marca é a única defesa que sobra se a âncora morrer num reboot.
  AppState.addEventListener("change", (state: AppStateStatus) => {
    if (state !== "active") {
      void persistirMarca(true);
      void persistirAncora(true);
    }
  });
};

/** Uptime atual (ms desde o boot) ou null sem o módulo nativo. */
export const uptimeAtual = (): number | null => TirvuClock.uptimeMs();

/**
 * Âncora utilizável AGORA + o uptime lido. Devolve null quando não há âncora, quando
 * não há módulo nativo, ou quando o uptime RETROCEDEU (reboot: âncora morta).
 */
const ancoraValida = (): { a: Ancora; up: number } | null => {
  const a = ancora;
  if (!a) return null;
  const up = TirvuClock.uptimeMs();
  if (up == null) return null;

  if (up < a.uptimeMs) {
    // Reboot óbvio: o contador zerou e ainda não passou do valor da âncora.
    descartarAncora();
    return null;
  }

  // ⚠️ AQUI ESTAVA O BUG QUE QUEBROU A PRODUÇÃO.
  //
  // A checagem `up < a.uptimeMs` acima só pega o reboot enquanto o aparelho está ligado
  // há MENOS tempo que o uptime gravado na âncora. O caso oposto passava batido:
  //
  //     âncora: uptime 10h, hora do servidor T0
  //     reboot  (uptime zera)
  //     ligado há 13h  ->  13h > 10h, a guarda dizia "válida"
  //     ms = T0 + (13h - 10h) = T0 + 3h,  quando a hora real era T0 + 15h
  //
  // Resultado: 12h de atraso, carimbado como `fonte: 'M'` (confiável). Só atingia quem
  // reiniciou o aparelho E abriu o app sem rede — por isso "só algumas pessoas".
  //
  // Não dá para consertar comparando relógio × uptime: num reboot e numa fraude de
  // relógio o relógio de parede anda mais que o uptime nos DOIS casos, então a guarda
  // que conserta um derruba o outro. A saída é provar a sessão de boot.
  if (!ancoraDestaSessao) {
    const bootAtual = TirvuClock.bootCount();
    // Só um contador de boot IGUAL prova que é a mesma sessão. `null` (iOS, ou Android
    // que não deixou ler) significa "não sei" — e "não sei" NUNCA autoriza carimbar.
    if (bootAtual == null || a.bootId == null || bootAtual !== a.bootId) {
      descartarAncora();
      return null;
    }
    // Provado: a âncora do disco é desta sessão de boot. Promove para não repetir a
    // consulta nativa a cada chamada de agora().
    ancoraDestaSessao = true;
  }

  return { a, up };
};

const descartarAncora = (): void => {
  ancora = null;
  ancoraDestaSessao = false;
  AsyncStorage.removeItem(CHAVE_ANCORA).catch(() => {});
};

/**
 * Registra um instante CONFIÁVEL na marca d'água.
 *
 * ⚠️ SÓ chamar com hora verificada (servidor ou âncora). Alimentar isto com
 * `Date.now()` cru deixaria alguém empurrar a marca para 2030 mexendo no relógio, e o
 * app ficaria permanentemente preso naquele instante — a marca é irreversível.
 */
const registrarMarca = (ms: number): void => {
  if (!Number.isFinite(ms) || ms <= marcaDagua) return;
  if (ms < MIN_PLAUSIVEL_MS || ms > MAX_PLAUSIVEL_MS) return;
  marcaDagua = ms;
  void persistirMarca(false);
};

/**
 * Régua para ESPAÇAR gravações no disco — só isso. Prefere o uptime; sem o módulo
 * nativo cai no relógio do aparelho, que aqui é inofensivo (adulterá-lo no máximo
 * antecipa/atrasa uma gravação, nunca muda a hora gravada). Sem este fallback, o modo
 * degradado escrevia no AsyncStorage a cada resposta da API.
 */
const relogioDeThrottle = (): number => TirvuClock.uptimeMs() ?? Date.now();

const persistirMarca = async (forcar: boolean): Promise<void> => {
  if (marcaDagua <= 0) return;
  const t = relogioDeThrottle();
  if (!forcar && t - marcaPersistidaEm < INTERVALO_PERSISTIR_MARCA_MS) return;
  marcaPersistidaEm = t;
  try {
    await AsyncStorage.setItem(CHAVE_MARCA, String(Math.floor(marcaDagua)));
  } catch {}
};

const persistirAncora = async (forcar: boolean): Promise<void> => {
  const a = ancora;
  if (!a) return;
  const t = relogioDeThrottle();
  if (!forcar && t - ancoraPersistidaEm < INTERVALO_PERSISTIR_ANCORA_MS) return;
  ancoraPersistidaEm = t;
  try {
    await AsyncStorage.setItem(CHAVE_ANCORA, JSON.stringify(a));
  } catch {}
};

/**
 * Reancora com a hora do servidor lida do header `Date` de uma resposta da NOSSA API.
 *
 * ⚠️ SÓ com resposta da nossa API. O header é uma afirmação de quem respondeu — vindo
 * de um host qualquer, seria o próprio vetor de fraude.
 *
 * `uptimeAntes` = uptime lido IMEDIATAMENTE antes do fetch. O header é gerado quando o
 * servidor começa a responder, ou seja, em algum ponto no meio do round-trip: ancorar
 * no meio (NTP-lite) troca um erro de "1 RTT inteiro" por "meio RTT". Em rede ruim isso
 * é a diferença entre errar 2s e errar 200ms — irrelevante para ponto, mas sai de graça.
 */
export const sincronizarComHeader = (
  headerDate: string | null | undefined,
  uptimeAntes: number | null,
): void => {
  if (!headerDate) return;

  const servidorMs = parseHttpDate(headerDate);
  if (!Number.isFinite(servidorMs)) return;
  if (servidorMs < MIN_PLAUSIVEL_MS || servidorMs > MAX_PLAUSIVEL_MS) return;

  // Vale mesmo SEM o módulo nativo: no modo degradado a marca d'água é a única
  // defesa, e ela só avança com hora verificada — esta é.
  registrarMarca(servidorMs);

  const uptimeDepois = TirvuClock.uptimeMs();
  if (uptimeDepois == null) return; // sem monotônico não há âncora possível

  const uptimeAncora =
    uptimeAntes != null && uptimeDepois >= uptimeAntes
      ? uptimeAntes + (uptimeDepois - uptimeAntes) / 2
      : uptimeDepois;

  ancora = {
    servidorMs,
    uptimeMs: uptimeAncora,
    dispositivoMs: Date.now(),
    bootId: TirvuClock.bootCount(),
  };
  // Nasceu NESTE processo: um reboot teria matado o processo, então é garantidamente
  // da sessão de boot atual. É a única âncora que dispensa prova.
  ancoraDestaSessao = true;
  void persistirAncora(false);
};

/**
 * HORA CONFIÁVEL (ms epoch). Substitui `Date.now()` em tudo que decide ponto.
 *
 * Com âncora válida: hora do servidor propagada pelo monotônico — imune ao relógio.
 * Sem âncora (degradado): `max(Date.now(), marcaDagua)` — não impede ADIANTAR o
 * relógio (o servidor barra isso na chegada), mas impede ATRASAR, que é a fraude
 * comum: chegar tarde e voltar o relógio para bater "no horário".
 */
export const agora = (): number => {
  const v = ancoraValida();
  if (v) {
    const ms = v.a.servidorMs + (v.up - v.a.uptimeMs);
    registrarMarca(ms);
    return ms;
  }
  return horaDegradada();
};

/** `agora()` como Date — substitui `new Date()` sem argumento. */
export const agoraDate = (): Date => new Date(agora());

/**
 * Diagnóstico completo — carimba a auditoria da batida (`fonteHora`/`divergenciaSeg`).
 *
 * ⚠️ NÃO serve para barrar ninguém. O relógio errado é resolvido em silêncio: a batida
 * SEMPRE passa, sempre com a hora confiável. Isto só registra de onde a hora veio, para
 * o RH enxergar depois — o colaborador não vê nada.
 */
export const estado = (): EstadoRelogio => {
  const v = ancoraValida();
  const dispositivo = Date.now();

  if (!v) {
    return {
      ms: horaDegradada(),
      fonte: "D",
      confiavel: false,
      // Sem âncora não há com o que comparar: divergência 0 significa "não sei"
      // (e não "relógio certo") — quem lê a auditoria se guia pelo `fonte: 'D'`.
      divergenciaSeg: 0,
      tipoDivergencia: "nenhuma",
      uptimeMs: TirvuClock.uptimeMs(),
      utcOffsetMin: new Date().getTimezoneOffset(),
    };
  }

  const ms = v.a.servidorMs + (v.up - v.a.uptimeMs);
  registrarMarca(ms);
  const idadeAncoraMs = v.up - v.a.uptimeMs;
  const divergenciaSeg = Math.round((dispositivo - ms) / 1000);

  return {
    ms,
    fonte: idadeAncoraMs <= FRESCOR_SERVIDOR_MS ? "S" : "M",
    confiavel: true,
    divergenciaSeg,
    tipoDivergencia: classificarDivergencia(divergenciaSeg),
    uptimeMs: v.up,
    utcOffsetMin: new Date().getTimezoneOffset(),
  };
};

/**
 * Há quanto tempo (ms) aconteceu algo carimbado com `uptimeMs` — medido pelo relógio
 * monotônico, sem passar pelo relógio de parede em momento nenhum.
 *
 * É a peça que permite ao servidor reconstruir a hora de uma batida OFFLINE:
 * `data_log = agoraServidor - idade`. Devolve null se não dá para afirmar (sem módulo
 * nativo, ou houve reboot entre o evento e agora).
 */
export const idadeMs = (
  uptimeNoEvento: number | null | undefined,
): number | null => {
  if (uptimeNoEvento == null || !Number.isFinite(uptimeNoEvento)) return null;
  const up = TirvuClock.uptimeMs();
  if (up == null || up < uptimeNoEvento) return null;
  return up - uptimeNoEvento;
};

export const RelogioConfiavel = {
  iniciar,
  agora,
  agoraDate,
  estado,
  sincronizarComHeader,
  uptimeAtual,
  idadeMs,
  classificarDivergencia,
  coerenciaFusoPosicao,
};
